import { Controller } from "@hotwired/stimulus"
import SignaturePad from "signature_pad"

export default class extends Controller {
  static targets = [
    "desenvWrapper", "identWrapper", "sigsWrapper",
    "supervisorName", "supervisorInput", "supervisorModal", "supervisorField",
    "signatureCanvas", "signatureModal",
    "collaboratorModal",
    "desenvTemplate", "identTemplate", "sigsTemplate",
    "desenvSubgroupTemplate", "identSubgroupTemplate",
    "desenvRowsField", "identRowsField", "sigsRowsField",
    "newCollabMatricula", "newCollabTipo", "newCollabName", "newCollabCargo"
  ]

  static values = {
    collaboratorsUrl: String,
    createCollaboratorUrl: String,
    customData: Object
  }

  connect() {
    this._collaborators = []
    this._signaturePad = null
    this._currentSigRow = null

    this._loadCollaborators()
    this._populateEditData()

    // Listen for serialize event from report-form controller
    this.element.closest("[data-report-form-target='aprFields']")
      ?.addEventListener("apr:serialize", () => this.serializeData())

    // Close popovers when clicking outside
    this._boundClosePopovers = (e) => this._closePopoversOnOutsideClick(e)
    document.addEventListener("click", this._boundClosePopovers)
  }

  disconnect() {
    document.removeEventListener("click", this._boundClosePopovers)
    if (this._signaturePad) {
      this._signaturePad.off()
      this._signaturePad = null
    }
  }

  // ── Collaborators ─────────────────────────────────────────────

  async _loadCollaborators() {
    if (!this.collaboratorsUrlValue) return
    try {
      const res = await fetch(this.collaboratorsUrlValue, { headers: { Accept: "application/json" } })
      if (res.ok) this._collaborators = await res.json()
    } catch (e) {
      console.error("Failed to load APR collaborators:", e)
    }
  }

  // ── Desenvolvimento da Atividade ──────────────────────────────

  addDesenvGroup(e) {
    if (e) e.preventDefault()
    const tmpl = this.desenvTemplateTarget.content.cloneNode(true)
    this.desenvWrapperTarget.appendChild(tmpl)
    return this.desenvWrapperTarget.lastElementChild
  }

  addDesenvSubgroup(e) {
    e.preventDefault()
    const group = e.target.closest(".apr-desenv-group")
    if (!group) return
    const subContainer = group.querySelector(".apr-subgroups")
    const tmpl = this.desenvSubgroupTemplateTarget.content.cloneNode(true)
    subContainer.appendChild(tmpl)
  }

  removeDesenvGroup(e) {
    e.preventDefault()
    e.target.closest(".apr-desenv-group")?.remove()
  }

  removeDesenvSubgroup(e) {
    e.preventDefault()
    e.target.closest(".apr-desenv-subgroup")?.remove()
  }

  // ── Identificacao ─────────────────────────────────────────────

  addIdentGroup(e) {
    if (e) e.preventDefault()
    const tmpl = this.identTemplateTarget.content.cloneNode(true)
    this.identWrapperTarget.appendChild(tmpl)
    return this.identWrapperTarget.lastElementChild
  }

  addIdentSubgroup(e) {
    e.preventDefault()
    const group = e.target.closest(".apr-ident-group")
    if (!group) return
    const subContainer = group.querySelector(".apr-subgroups")
    const tmpl = this.identSubgroupTemplateTarget.content.cloneNode(true)
    subContainer.appendChild(tmpl)
  }

  removeIdentGroup(e) {
    e.preventDefault()
    e.target.closest(".apr-ident-group")?.remove()
  }

  removeIdentSubgroup(e) {
    e.preventDefault()
    e.target.closest(".apr-ident-subgroup")?.remove()
  }

  // ── Comissao (Signature rows) ─────────────────────────────────

  addSigsRow(e) {
    if (e) e.preventDefault()
    const tmpl = this.sigsTemplateTarget.content.cloneNode(true)
    this.sigsWrapperTarget.appendChild(tmpl)
    return this.sigsWrapperTarget.lastElementChild
  }

  removeSigsRow(e) {
    e.preventDefault()
    e.target.closest(".apr-sigs-row")?.remove()
  }

  // ── Collaborator Popover Dropdown ─────────────────────────────

  toggleCollabPopover(e) {
    e.stopPropagation()
    const row = e.target.closest(".apr-sigs-row")
    if (!row) return

    const popover = row.querySelector(".apr-collab-popover")
    if (!popover) return

    // Close all other popovers first
    this._closeAllPopovers(popover)

    if (popover.classList.contains("hidden")) {
      this._openCollabPopover(row, popover)
    } else {
      popover.classList.add("hidden")
    }
  }

  _openCollabPopover(row, popover) {
    const searchInput = popover.querySelector(".apr-collab-search")
    if (searchInput) searchInput.value = ""
    this._renderCollabList(popover, "")
    popover.classList.remove("hidden")
    if (searchInput) setTimeout(() => searchInput.focus(), 50)
  }

  filterCollabPopover(e) {
    e.stopPropagation()
    const popover = e.target.closest(".apr-collab-popover")
    if (!popover) return
    const query = e.target.value.trim().toLowerCase()
    this._renderCollabList(popover, query)
  }

  _renderCollabList(popover, query) {
    const listEl = popover.querySelector(".apr-collab-list")
    if (!listEl) return

    const filtered = this._collaborators.filter(c => {
      if (!query) return true
      return `${c.matricula} ${c.name}`.toLowerCase().includes(query)
    }).slice(0, 30)

    if (filtered.length === 0 && !query) {
      listEl.innerHTML = `<div class="px-3 py-4 text-xs text-gray-400 text-center">Nenhum colaborador encontrado</div>`
    } else if (filtered.length === 0) {
      listEl.innerHTML = `<div class="px-3 py-4 text-xs text-gray-400 text-center">Nenhum resultado</div>`
    } else {
      listEl.innerHTML = filtered.map(c =>
        `<div class="apr-collab-item flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
              data-matricula="${this._esc(c.matricula || '')}"
              data-tipo="${this._esc(c.tipo || '')}"
              data-name="${this._esc(c.name || '')}">
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-gray-900">${this._esc(c.matricula || '')}</div>
            <div class="text-xs text-gray-500">${this._esc(c.name || '')}</div>
          </div>
        </div>`
      ).join("")

      listEl.querySelectorAll(".apr-collab-item").forEach(item => {
        item.addEventListener("click", (ev) => {
          ev.stopPropagation()
          const row = popover.closest(".apr-sigs-row")
          this._selectCollaborator(row, {
            matricula: item.dataset.matricula,
            tipo: item.dataset.tipo,
            name: item.dataset.name
          })
          popover.classList.add("hidden")
        })
      })
    }
  }

  _selectCollaborator(row, collab) {
    if (!row) return
    const display = row.querySelector("[data-field='matricula-display']")
    const hidden = row.querySelector("[data-field='matricula']")
    const tipo = row.querySelector("[data-field='tipo']")
    const nome = row.querySelector("[data-field='nome']")

    if (display) {
      display.value = collab.matricula
      display.classList.remove("text-text-muted")
      display.classList.add("text-gray-900")
    }
    if (hidden) hidden.value = collab.matricula
    if (tipo) tipo.value = collab.tipo || ""
    if (nome) nome.value = collab.name || ""
  }

  openCollaboratorFromPopover(e) {
    e.preventDefault()
    e.stopPropagation()
    // Store reference to the row that triggered the popover
    this._popoverOriginRow = e.target.closest(".apr-sigs-row")
    this._closeAllPopovers()
    this.openCollaboratorModal(e)
  }

  _closeAllPopovers(except) {
    this.element.querySelectorAll(".apr-collab-popover").forEach(p => {
      if (p !== except) p.classList.add("hidden")
    })
  }

  _closePopoversOnOutsideClick(e) {
    if (!e.target.closest(".apr-collab-popover") && !e.target.matches("[data-field='matricula-display']")) {
      this._closeAllPopovers()
    }
  }

  // ── Signature Pad ─────────────────────────────────────────────

  openSignaturePad(e) {
    e.preventDefault()
    this._currentSigRow = e.target.closest(".apr-sigs-row")
    this.signatureModalTarget.classList.remove("hidden")

    // Initialize signature pad (must be done after modal is visible)
    requestAnimationFrame(() => {
      const canvas = this.signatureCanvasTarget
      canvas.width = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
      if (this._signaturePad) {
        this._signaturePad.clear()
      } else {
        this._signaturePad = new SignaturePad(canvas, {
          backgroundColor: "rgb(255, 255, 255)"
        })
      }
    })
  }

  clearSignature(e) {
    e.preventDefault()
    if (this._signaturePad) this._signaturePad.clear()
  }

  saveSignature(e) {
    e.preventDefault()
    if (!this._signaturePad || this._signaturePad.isEmpty()) return

    const dataUrl = this._signaturePad.toDataURL()
    if (this._currentSigRow) {
      const preview = this._currentSigRow.querySelector("[data-field='sig-preview']")
      const button = this._currentSigRow.querySelector("[data-field='sig-button']")

      if (preview) {
        preview.src = dataUrl
        preview.classList.remove("hidden")
      }
      // Hide the Assinar button, signature replaces it
      if (button) button.classList.add("hidden")

      // Store base64 data on the row
      this._currentSigRow.dataset.signatureData = dataUrl
    }

    this.closeSignatureModal()
  }

  closeSignatureModal() {
    this.signatureModalTarget.classList.add("hidden")
    this._currentSigRow = null
  }

  // ── Supervisor Name ───────────────────────────────────────────

  openSupervisorModal(e) {
    e.preventDefault()
    this.supervisorInputTarget.value = this.supervisorNameTarget.textContent.trim()
    this.supervisorModalTarget.classList.remove("hidden")
    this.supervisorInputTarget.focus()
  }

  saveSupervisorName(e) {
    e.preventDefault()
    const name = this.supervisorInputTarget.value.trim()
    this.supervisorNameTarget.textContent = name
    this.supervisorFieldTarget.value = name
    this.closeSupervisorModal()
  }

  closeSupervisorModal() {
    this.supervisorModalTarget.classList.add("hidden")
  }

  // ── Add Collaborator ──────────────────────────────────────────

  openCollaboratorModal(e) {
    e.preventDefault()
    this.newCollabMatriculaTarget.value = ""
    this.newCollabTipoTarget.value = ""
    this.newCollabNameTarget.value = ""
    this.newCollabCargoTarget.value = ""
    this.collaboratorModalTarget.classList.remove("hidden")
    this.newCollabMatriculaTarget.focus()
  }

  async saveCollaborator(e) {
    e.preventDefault()
    const matricula = this.newCollabMatriculaTarget.value.trim()
    const tipo = this.newCollabTipoTarget.value.trim()
    const name = this.newCollabNameTarget.value.trim()
    const cargo = this.newCollabCargoTarget.value.trim()

    if (!matricula || !name) return

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.createCollaboratorUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          Accept: "application/json"
        },
        body: JSON.stringify({ matricula, tipo, name, cargo })
      })
      if (res.ok) {
        const newCollab = { matricula, tipo, name }
        this._collaborators.push(newCollab)
        this.closeCollaboratorModal()

        // If opened from a popover, auto-select the new collaborator in that row
        if (this._popoverOriginRow) {
          this._selectCollaborator(this._popoverOriginRow, newCollab)
          this._popoverOriginRow = null
        }
      }
    } catch (err) {
      console.error("Failed to create collaborator:", err)
    }
  }

  closeCollaboratorModal() {
    this.collaboratorModalTarget.classList.add("hidden")
  }

  // ── Serialization ─────────────────────────────────────────────

  serializeData() {
    // Serialize Desenvolvimento rows
    const desenvRows = []
    this.desenvWrapperTarget.querySelectorAll(".apr-desenv-group").forEach(group => {
      const row = {
        field1: group.querySelector("[data-field='field1']")?.value || "",
        field2: group.querySelector("[data-field='field2']")?.value || "",
        field3: group.querySelector("[data-field='field3']")?.value || "",
        subgroups: []
      }
      group.querySelectorAll(".apr-desenv-subgroup").forEach(sub => {
        row.subgroups.push({
          field2: sub.querySelector("[data-field='field2']")?.value || "",
          field3: sub.querySelector("[data-field='field3']")?.value || ""
        })
      })
      desenvRows.push(row)
    })

    // Serialize Identificacao rows
    const identRows = []
    this.identWrapperTarget.querySelectorAll(".apr-ident-group").forEach(group => {
      const row = {
        field1: group.querySelector("[data-field='field1']")?.value || "",
        field2: group.querySelector("[data-field='field2']")?.value || "",
        field3: group.querySelector("[data-field='field3']")?.value || "",
        subgroups: []
      }
      group.querySelectorAll(".apr-ident-subgroup").forEach(sub => {
        row.subgroups.push({
          field2: sub.querySelector("[data-field='field2']")?.value || "",
          field3: sub.querySelector("[data-field='field3']")?.value || ""
        })
      })
      identRows.push(row)
    })

    // Serialize Comissao rows
    const sigsRows = []
    this.sigsWrapperTarget.querySelectorAll(".apr-sigs-row").forEach(row => {
      sigsRows.push({
        field1: row.querySelector("[data-field='matricula']")?.value || "",
        field2: row.querySelector("[data-field='tipo']")?.value || "",
        field3: row.querySelector("[data-field='nome']")?.value || "",
        field4: row.dataset.signatureData || ""
      })
    })

    // Set hidden field values
    this.desenvRowsFieldTarget.value = JSON.stringify(desenvRows)
    this.identRowsFieldTarget.value = JSON.stringify(identRows)
    this.sigsRowsFieldTarget.value = JSON.stringify(sigsRows)

    // Supervisor name
    if (this.hasSupervisorNameTarget) {
      this.supervisorFieldTarget.value = this.supervisorNameTarget.textContent.trim()
    }
  }

  // ── Edit mode population ──────────────────────────────────────

  _populateEditData() {
    const data = this.customDataValue
    if (!data || Object.keys(data).length === 0) {
      // Add one empty row for each section in new mode
      this.addDesenvGroup()
      this.addIdentGroup()
      this.addSigsRow()
      return
    }

    // Supervisor name
    if (data.apr_supervisor_nome && this.hasSupervisorFieldTarget) {
      this.supervisorFieldTarget.value = data.apr_supervisor_nome
    }

    // Desenvolvimento rows
    if (data.apr_desenv_rows && data.apr_desenv_rows.length > 0) {
      data.apr_desenv_rows.forEach(rowData => {
        const group = this.addDesenvGroup()
        if (!group) return
        const f1 = group.querySelector("[data-field='field1']")
        const f2 = group.querySelector("[data-field='field2']")
        const f3 = group.querySelector("[data-field='field3']")
        if (f1) f1.value = rowData.field1 || ""
        if (f2) f2.value = rowData.field2 || ""
        if (f3) f3.value = rowData.field3 || ""

        if (rowData.subgroups) {
          rowData.subgroups.forEach(sg => {
            const subContainer = group.querySelector(".apr-subgroups")
            const tmpl = this.desenvSubgroupTemplateTarget.content.cloneNode(true)
            subContainer.appendChild(tmpl)
            const subEl = subContainer.lastElementChild
            const sf2 = subEl.querySelector("[data-field='field2']")
            const sf3 = subEl.querySelector("[data-field='field3']")
            if (sf2) sf2.value = sg.field2 || ""
            if (sf3) sf3.value = sg.field3 || ""
          })
        }
      })
    } else {
      this.addDesenvGroup()
    }

    // Identificacao rows
    if (data.apr_ident_rows && data.apr_ident_rows.length > 0) {
      data.apr_ident_rows.forEach(rowData => {
        const group = this.addIdentGroup()
        if (!group) return
        const f1 = group.querySelector("[data-field='field1']")
        const f2 = group.querySelector("[data-field='field2']")
        const f3 = group.querySelector("[data-field='field3']")
        if (f1) f1.value = rowData.field1 || ""
        if (f2) f2.value = rowData.field2 || ""
        if (f3) f3.value = rowData.field3 || ""

        if (rowData.subgroups) {
          rowData.subgroups.forEach(sg => {
            const subContainer = group.querySelector(".apr-subgroups")
            const tmpl = this.identSubgroupTemplateTarget.content.cloneNode(true)
            subContainer.appendChild(tmpl)
            const subEl = subContainer.lastElementChild
            const sf2 = subEl.querySelector("[data-field='field2']")
            const sf3 = subEl.querySelector("[data-field='field3']")
            if (sf2) sf2.value = sg.field2 || ""
            if (sf3) sf3.value = sg.field3 || ""
          })
        }
      })
    } else {
      this.addIdentGroup()
    }

    // Comissao (sigs) rows
    if (data.apr_sigs_rows && data.apr_sigs_rows.length > 0) {
      data.apr_sigs_rows.forEach(rowData => {
        const row = this.addSigsRow()
        if (!row) return
        const display = row.querySelector("[data-field='matricula-display']")
        const hidden = row.querySelector("[data-field='matricula']")
        const tipoInput = row.querySelector("[data-field='tipo']")
        const nomeInput = row.querySelector("[data-field='nome']")
        const sigPreview = row.querySelector("[data-field='sig-preview']")
        const sigButton = row.querySelector("[data-field='sig-button']")

        if (display) display.value = rowData.field1 || ""
        if (hidden) hidden.value = rowData.field1 || ""
        if (tipoInput) tipoInput.value = rowData.field2 || ""
        if (nomeInput) nomeInput.value = rowData.field3 || ""

        if (rowData.field4 && sigPreview) {
          sigPreview.src = rowData.field4
          sigPreview.classList.remove("hidden")
          if (sigButton) sigButton.classList.add("hidden")
          row.dataset.signatureData = rowData.field4
        }
      })
    } else {
      this.addSigsRow()
    }
  }

  // ── Utilities ─────────────────────────────────────────────────

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
